<i18n>
{
	"en": {
		"theTripTemplate": "the trip template",
		"addRemove": "Add order templates",
		"cancelLabel": "Cancel",
		"editTemplate": "Edit Trip Template",
		"failedToCalculate": "Failed to calculate next trip",
		"failedToCreate": "Failed to create new template",
		"failedToFetch": "Failed to fetch trips for this template",
		"failedToUpdate": "Failed to update trip template",
		"generatedTrips": "Generated trips",
		"newTemplate": "New Trip Template",
		"newTemplateSaved": "Trip Template Saved",
		"allreadyUpToDate": "No changes has been done",
		"removeAll": "Disconnect all trips",
		"save": "Save",
		"saveAndClose": "Save & close",
		"seeTrip": "See trip",
		"templateData": "Template info",
		"autoSchedule": "Auto generation",
		"validationErrors": "Validation error. To save the trip template the errors must be corrected as described in the schema.",
		"orderTemplates": "Orders on template"
	},

	"no": {
		"theTripTemplate": "turmalen",
		"addRemove": "Legg til oppdragsmaler",
		"cancelLabel": "Avbryt",
		"editTemplate": "Rediger Turmal",
		"failedToCalculate": "Kunne ikke beregne neste tur",
		"failedToCreate": "Kunne ikke opprette ny mal",
		"failedToFetch": "Kunne ikke hente turer for denne malen",
		"failedToUpdate": "Kunne ikke oppdatere turmal",
		"generatedTrips": "Genererte turer",
		"newTemplate": "Ny Turmal",
		"newTemplateSaved": "Turmal Lagret",
		"allreadyUpToDate": "Ingen endringer er gjort",
		"removeAll": "Fjern alle koblinger",
		"save": "Lagre",
		"saveAndClose": "Lagre & lukk",
		"seeTrip": "Se tur",
		"templateData": "Malinformasjon",
		"autoSchedule": "Autogenerering",
		"validationErrors": "Feil ved validering. For å lagre turmalen må feilene rettes slik som angitt i skjemaet.",
		"orderTemplates": "Oppdrag på mal"
	}
}
</i18n>

<template>
	<t-dialog
		:isShown="isShown"
		:title="templateDetails.id ? $t('editTemplate') : $t('newTemplate')"
		:confirmLabel="$t('save')"
		:cancelLabel="$t('cancelLabel')"
		:icon="templateDetails.id ? 'trip' : 'trip-new'"
		disableCloseOnConfirm
		alwaysRememberFocus
		:keyListeners="getKeyListeners()"
		@closing="checkClose"
		@close="close"
	>

		<t-tab-list :items="tabs" :selected.sync="selectedTab" style="flex-basis: auto;">
			<template #default="{ item, isSelected }">
				<t-tab :value="item" :selected="isSelected">
					{{ $t(item.id) }}
				</t-tab>
			</template>
		</t-tab-list>
		<hr>
		<keep-alive>
			<component :is="selectedTab.component" :ref="selectedTab.id" class="trip-template-dialog-inner" />
		</keep-alive>

		<template #footer>
			<div style="width: 100%; display: flex; justify-content: space-between">
				<div v-if="selectedTab.id === 'generatedTrips' && templateDetails.generatedTrips.length" class="button-group">
					<t-button
						iconBefore="trash"
						intent="danger"
						appearance="primary"
						@click="removeAllTrips"
					>
						{{ $t('removeAll') }}
					</t-button>
					<t-button
						iconBefore="th-derived"
						@click="$refs.generatedTrips.showTrip()"
					>
						{{ $t('seeTrip') }}
					</t-button>
				</div>

				<div v-else-if="selectedTab.id === 'orderTemplates'">
					<t-button
						iconBefore="annotation"
						@click="addOrderTemplate"
					>{{ $t('addRemove') }}</t-button>
				</div>

				<div v-else style="display:flex"/>

				<div style="display:flex">
					<t-button
						:value="$t('cancelLabel')"
						appearance="default"
						ointent="default"
						@click="onCancel"
					/>
					<t-button
						:value="$t('saveAndClose')"
						:appearance="'primary'"
						style="margin-left: 15px;"
						@click="saveAndClose()"
					/>
					<t-button
						:value="$t('save')"
						:appearance="'primary'"
						style="margin-left: 15px;"
						@click="save()"
					/>
				</div>
			</div>
		</template>

		<ConfirmUnsavedChanges
			:isShown="confirmUnsavedChanges"
			:subject="$t('theTripTemplate')"
			@cancel="confirmUnsavedChanges = false"
			@save="saveAndClose"
			@close="onCloseDirty"
		/>

		<AddOrderTemplate />
	</t-dialog>
</template>

<script>
import {
	mapState, mapActions, mapMutations
} from 'vuex';
import { eventBus, delay } from '@/core';

import TripTemplateData from './TripTemplateDialog/TripTemplateData.vue';
import GeneratedTrips from './TripTemplateDialog/GeneratedTrips.vue';
import OrdersOnTemplate from './TripTemplateDialog/OrdersOnTemplate.vue';
import AddOrderTemplate from './TripTemplateDialog/AddOrderTemplate.vue';
import FrequencySelector from './TripTemplateDialog/FrequencySelector';
import ConfirmUnsavedChanges from '@/components/modals/ConfirmUnsavedChanges';

export default {
	name: 'TripTemplateDialog',
	components: { AddOrderTemplate, ConfirmUnsavedChanges },

	data() {
		return {
			isShown: false,
			tab: undefined,
			confirmUnsavedChanges: false,
			closeDirty: false,
		};
	},

	computed: {
		...mapState('tripTemplates', [ 'templateDetails', 'generatedTrips', 'dirty' ]),
		tabs() {
			return [{
				component: TripTemplateData,
				id: 'templateData',
			}, {
				component: FrequencySelector,
				id: 'autoSchedule',
			}, {
				component: OrdersOnTemplate,
				id: 'orderTemplates',
			}, {
				component: GeneratedTrips,
				id: 'generatedTrips',
			}];
		},
		selectedTab: {
			get() {
				return this.tab || this.tabs[0];
			},
			set(value) {
				this.tab = value;
			}
		}
	},

	watch: {
		isShown: {
			async handler(newVal) {
				if (newVal) {
					if (this.templateDetails.generatedTrips.length) {
						try {
							await this.fetchGeneratedTrips();
						} catch (err) {
							this.$notify({
								type: 'error',
								title: this.$t(err.message),
								duration: -1
							});
						}
					}
				}
			}
		}
	},

	created() {
		eventBus.$on('toggleTripTemplateDetailsModal', this.toggleModal);
		eventBus.$on('setTripTemplateDetails', this.setTemplateToEdit);
	},

	beforeDestroy() {
		eventBus.$off('toggleTripTemplateDetailsModal');
		eventBus.$off('setTripTemplateDetails');
	},

	methods: {
		...mapActions('tripTemplates', [ 'saveTripTemplate', 'fetchGeneratedTrips', 'findNextTrip', 'setGeneratedTrips', ]),
		...mapActions('orderTemplates', ['getOrderTemplates']),
		...mapMutations('tripTemplates', [ 'resetDetails', 'setTemplateToEdit', ]),
		toggleModal(value) {
			this.isShown = value;
		},
		removeAllTrips() {
			this.setGeneratedTrips([]);
		},
		addOrderTemplate() {
			this.getOrderTemplates();
			eventBus.$emit('addOrderTemplate', true);
		},

		async close() {
			this.confirmUnsavedChanges = false;
			this.toggleModal(false);
			await delay(250); // Dialog uses some time to close, and it doesn't look nice to see stuff changing just before it closes.
			this.$notify({ clean: true });
			this.resetDetails();
			this.selectedTab = this.tabs[0];
		},

		onCancel() {
			if (this.dirty && !this.closeDirty) {
				this.confirmUnsavedChanges = true;
			} else {
				this.close();
			}
		},
		checkClose(abort) {
			if (this.dirty && !this.closeDirty) {
				abort(true);
				this.confirmUnsavedChanges = true;
			} else {
				abort(false);
			}
			this.closeDirty = false;
		},
		onCloseDirty() {
			this.closeDirty = true;
			this.close();
		},

		async save() {
			try {
				let allreadyUpToDate = await this.saveTripTemplate();
				console.log(`allreadyUpToDate: ${allreadyUpToDate}`);
				if (allreadyUpToDate) {
					this.$notify({
						type: 'success',
						title: this.$t('allreadyUpToDate'),
					});
				} else {
					this.$notify({
						type: 'success',
						title: `${this.$t('newTemplateSaved')}: ${this.templateDetails.name}`
					});
				}
				return true;
			} catch (err) {
				this.$notify({
					type: 'error',
					title: this.$t(err.message),
					duration: -1
				});
				return false;
			}
		},

		async saveAndClose() {
			let success = await this.save();
			if (success) this.close();
		},

		getKeyListeners() {
			return [
				{ 	key: 'S',
					listener: this.save,
					modifiers: { ctrlKey: true, shiftKey: undefined, altKey: false, stop: true, prevent: true } },
				{ 	key: 'Enter',
					listener: this.saveAndClose,
					modifiers: { ctrlKey: true, shiftKey: false, altKey: false, stop: true, prevent: true } },
			];
		},
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.trip-template-dialog-inner {
	width: 1000px;
	padding: 0 10px 0 5px;
}

.button-group {
	display: flex;
	flex-direction: row;
	> * {
		margin-right: 12px;
	}
}

hr {
	grid-column: 1 / auto-fill;
	height: 1px;
	background: get-color($border, 'default');
	width: 100%;
	margin-top: 0;
	margin-bottom: 10px;
	border: 0;
}
</style>
